/**
 * The permission catalogue.
 *
 * Fixed in code on purpose: a permission only means something if some code
 * checks it, so an admin cannot invent one. What admins do compose freely is
 * ROLES — named bundles of these — and which members hold them.
 *
 * Doing ordinary member things (creating a bill, paying, approving payments on
 * a bill you fronted) needs no permission at all; that is the `session` level.
 */
export const PERMISSIONS = {
    'members.manage': {
        label: 'Quản lý thành viên',
        description: 'Thêm, sửa, mời, ngừng/kích hoạt thành viên và gán vai trò.',
        group: 'Thành viên',
    },
    'members.resetPassword': {
        label: 'Gửi mail đặt lại mật khẩu',
        description: 'Gửi cho thành viên link đặt mật khẩu mới (sống 15 phút).',
        group: 'Thành viên',
        // A link that sets someone's password is a way into their account: administrators only (decided 29/09/2026).
        adminOnly: true,
    },
    'generations.manage': {
        label: 'Quản lý gen & bàn giao',
        description: 'Chuyển sang gen mới: thêm thành viên mới, chuyển người rời đi thành cựu thành viên, chuyển giao vai trò.',
        group: 'Thành viên',
    },
    'teams.manage': {
        label: 'Quản lý ban',
        description: 'Tạo, đổi tên, sắp xếp, lưu trữ ban và xếp thành viên vào ban. Ban chỉ để phân loại thành viên, không cấp quyền nào.',
        group: 'Thành viên',
    },
    'roles.manage': {
        label: 'Quản lý vai trò',
        description: 'Tạo, sửa, xoá vai trò và chọn quyền cho từng vai trò.',
        group: 'Thành viên',
    },
    'bills.approve': {
        label: 'Duyệt khoản',
        description: 'Duyệt/từ chối khoản chờ duyệt, trả lại tiền cho người ứng từ quỹ, sửa và huỷ khoản của người khác. Khoản do người có quyền này tạo được duyệt ngay.',
        group: 'Tài chính',
    },
    'bills.viewAll': {
        label: 'Xem mọi khoản của CLB',
        description: 'Xem các thanh toán đang chờ và khoản của người khác trong Duyệt, chỉ để xem. Không duyệt được gì thêm.',
        group: 'Tài chính',
    },
    'reports.view': {
        label: 'Xem thống kê',
        description: 'Xem Thống kê và báo cáo Thu/Chi của toàn CLB.',
        group: 'Tài chính',
    },
    'reports.team': {
        label: 'Xem thống kê ban mình',
        description: 'Xem bảng Thống kê → Thành viên của ban mình đang ở (điểm danh, công việc, đóng góp) và hoạt động của từng người trong ban. Ban chỉ giới hạn phạm vi xem; quyền vẫn đến từ vai trò.',
        group: 'Tài chính',
    },
    'posts.manage': {
        label: 'Quản lý bài viết',
        description: 'Viết, sửa, xuất bản, gỡ, xoá bài viết và tải ảnh lên.',
        group: 'Nội dung',
    },
    'site.manage': {
        label: 'Sửa nội dung trang',
        description: 'Sửa nội dung landing page và trang HBAAC: số liệu, hoạt động, đợt tuyển thành viên, lịch thi, giải thưởng, FAQ.',
        group: 'Nội dung',
    },
    'events.manage': {
        label: 'Quản lý sự kiện & điểm danh',
        description: 'Tạo sự kiện, cuộc họp; trình chiếu QR check-in; check-in hộ, huỷ và xem báo cáo điểm danh.',
        group: 'Thành viên',
    },
    'projects.create': {
        label: 'Tạo dự án',
        description: 'Tạo dự án mới và làm quản lý dự án mình tạo. Không có quyền gì trên dự án của người khác.',
        group: 'Thành viên',
    },
    'tasks.manage': {
        label: 'Quản lý mọi dự án & công việc',
        description: 'Làm quản lý ở mọi dự án của CLB: thành viên dự án, sửa, xoá, duyệt task, lưu trữ. Dành cho Ban Chủ nhiệm.',
        group: 'Thành viên',
    },
    'docs.manage': {
        label: 'Quản lý tài liệu',
        description: 'Tạo, sửa, ghim, xoá mọi tài liệu (quy trình, biểu mẫu, hướng dẫn) và chọn ai được đọc, ai được sửa từng tài liệu.',
        group: 'Nội dung',
    },
    'mail.campaigns': {
        label: 'Gửi mail hàng loạt',
        description: 'Soạn và gửi mail theo danh sách hoặc Google Sheet (người ngoài CLB), cài mail tự động khi có người điền form.',
        group: 'Nội dung',
    },
    'notifications.send': {
        label: 'Gửi thông báo',
        description: 'Soạn và gửi thông báo cho cả CLB, theo ban, theo vai trò hoặc từng người.',
        group: 'Nội dung',
    },
    'settings.manage': {
        label: 'Cấu hình hệ thống',
        description: 'Sửa cấu hình, bật/tắt tác vụ tự động và cấu hình email.',
        group: 'Hệ thống',
    },
    'logs.view': {
        label: 'Xem nhật ký',
        description: 'Xem nhật ký hệ thống.',
        group: 'Hệ thống',
        // The log names who did what, including refused attempts: only administrators read it.
        adminOnly: true,
    },
};
export const PERMISSION_NAMES = Object.keys(PERMISSIONS);
export function isPermission(p) {
    return typeof p === 'string' && Object.prototype.hasOwnProperty.call(PERMISSIONS, p);
}
/**
 * Whether a permission can be put in an ordinary role. Admin-only ones are held
 * through the built-in "Quản trị viên" role alone: never offered, never granted,
 * and ignored if a custom role somehow carries one.
 */
export function isGrantable(p) {
    return !('adminOnly' in PERMISSIONS[p]);
}
//# sourceMappingURL=permissions.js.map